<template>
  <td :class="{'day':true, 'greyed-out': greyedOut && !weeklyView,
               'highlighted': todaysDate === getStringDate(),
               'weekViewDay': weeklyView}">

    <!-- monthly view -->
    <div v-if="!weeklyView">
      <div class="date">{{ date }}</div>
      <div class="events">
      <Event v-for="ev in removeDuplicates(events)" :key="'event-'+ev.id"
            :id="'event-'+ev.id" :info="ev" :weeklyView="weeklyView"
             v-on:detailed-open="detailedOpen = true, detailedInfo = ev"
             v-on:detailed-close="startDetailClose"/>
      </div>
    </div>

    <!-- weekly view -->
    <div v-else class="hours-section">
      <table v-if="events.length > 0" class="hours-table">
        <!-- divide events into time segments (each table row = 1/2 hour)-->
        <tr v-for="(h, i) in getEventsDict(events)" :key="'hour-'+i"
            :class="{'hour-line':i%2 === 1}">
          <td v-for="(ev, i2) in h" :key="'hour-event'+i2"
              :id="'event-'+ev.id"
              :rowspan="ev.rowspan" :colspan="calcColSpan(ev)"
              :style="{ 'max-width': calcMaxWidth(ev.concurrent) }">
            <Event :info="ev" class="weeklyViewEvent" :weeklyView="weeklyView"
                   v-on:detailed-open="detailedOpen = true, detailedInfo = ev"
                   v-on:detailed-close="startDetailClose"
                   :style="{ height: ev.rowspan * 28 - 12 + 'px'}"/>
                   <!-- Note on (:style):
                        * .hours-table tr has height = 28, so multiply to get
                          final height for events spanning > 1 row
                        * -12 to adjust for borders -->
          </td>
          <td v-if="h.length === 0" :colspan="maxConcurrent"></td>
        </tr>
      </table>

      <!-- draw in hours lines even if no events on day -->
      <table v-else class="hours-table">
        <tr v-for="i in totalHalfHours" :key="'hour' + i"
            :class="{'hour-line':(i-1)%2 === 1}"><td><br></td></tr>
      </table>
    </div>

    <!-- show popup -->
    <DetailBox v-if="detailedOpen || rolloverDetailed"
               v-on:rollover-detailed="rolloverDetailed = true"
               v-on:detailed-close="detailedOpen = false, rolloverDetailed = false"
               :info="detailedInfo"
               :displayYear="displayYear"
               :weeklyView="weeklyView"/>
  </td>
</template>

<script>
import Event from '../components/Event.vue'
import DetailBox from '../components/DetailBox.vue'

export default {
  name: 'Week',
  components: {
    DetailBox,
    Event,
  },
  props: {
    info: { type: Array, required: true },
    date: Number,
    greyedOut: Boolean,
    displayYear: Number,
    displayMonth: Number,
    todaysDate: String,
    weeklyView: Boolean
  },
  data() {
    return {
      detailedOpen: false, // hovering over event
      rolloverDetailed: false, // hovering over detail box
      detailedInfo: {},  // info to pass to detailed view
      maxConcurrent: 0,
      totalHalfHours: 48,
    }
  },
  methods: {
    getStringDate() {
      let mm = (this.displayMonth < 10) ? ("0"+this.displayMonth) : this.displayMonth;
      let dd = (this.date < 10) ? ("0"+this.date) : this.date;
      return this.displayYear + "-" + mm + "-" + dd;
    },
    startDetailClose() { // leave time for potential rollover onto popup
      setTimeout(() => { if (!this.rolloverDetailed) { this.detailedOpen = false }}, 100)
    },
    removeDuplicates(events) { // remove duplicates by title
      let eventsDict = {};
      for (let i = 0; i < events.length; i++) {
        let title = events[i].title;
        /*
        if (title in eventsDict &&
            eventsDict[title].last_modified > events[i].last_modified) {
            // if duplicates, use the most recent
          continue;
        }
        */
        if (title in eventsDict && eventsDict[title].location !== null &&
            eventsDict[title].location.includes('zoom') &&
            this.displayYear === 2020 && this.displayMonth <= 7) {
            // temporary workaround for duplicate office hrs, can remove section
          continue;
        }
        eventsDict[title] = events[i];
      }
      var retEvents = Object.keys(eventsDict).map( function(k) {
                        return eventsDict[k];
                      });
      return retEvents;
    },
    getEventsDict(events) {
      /* organizes events into their respective timeslots,
         handles overlapping events
         returns: { tableRow : [ event, ... ] } */

      let eventsDict = {}; // { time/tr index : [ event ] }; (ea. event only appears in FIRST row / start time)
      let calcConcurrentDict = {}; // separate copy of eventsDict (ea. event appears in ALL rows in timespan)

      let eventToOverlapDict = {}; // { eventId: numOverlap } (holds # events overlapping given event)
      let countConcurrent = 1; // determines max number of columns in day (min = 1, even if 0 events)

      for (let i = 0; i < this.totalHalfHours; i++) { // 48 half hour slots
        eventsDict[i] = [];
        calcConcurrentDict[i] = [];
      }

      for (let i = 0; i < events.length; i++) {
        let ev = events[i];

        // convert to current time zone
        let startTime = new Date(0);
        startTime.setUTCSeconds(parseInt(ev['date_ts']));
        let endTime = new Date(0);
        endTime.setUTCSeconds(parseInt(ev['date2_ts']));

        // determine table row position in calendar (1 table row = 1/2 hour)
        let enteredStart = (startTime.getHours() * 60 + startTime.getMinutes()) / 30;
        let enteredEnd = (endTime.getHours() * 60 + endTime.getMinutes()) / 30;
        let timeSpan = enteredEnd - enteredStart;
        ev['rowspan'] = timeSpan;

        for (let t = enteredStart; t < enteredEnd; t++) {
          // add info to dictionaries for each table row
          let existingEvents = eventsDict[t];
          let concurrentEvents = calcConcurrentDict[t];
          if (t === enteredStart) {
            existingEvents.push(ev);
          }
          concurrentEvents.push(ev);
          eventsDict[t] = existingEvents;
          calcConcurrentDict[t] = concurrentEvents;

          countConcurrent = Math.max(countConcurrent, concurrentEvents.length+1);

          // for every event overlapping with current event,
          // update eventToOverlapDict with its overlap count
          for (let eventI = 0; eventI < concurrentEvents.length; eventI++) {
            let currentId = concurrentEvents[eventI].id;
            if (currentId in eventToOverlapDict) {
              eventToOverlapDict[currentId] = Math.max(concurrentEvents.length,
                                                       eventToOverlapDict[currentId]);
            } else {
              eventToOverlapDict[currentId] = calcConcurrentDict[t].length;
            }
          }
        }
      }

      // for every event in each time segment,
      // save final # of overlaps from eventToOverlapDict to its properties
      for (let t = 0; t < this.totalHalfHours; t++) {
        for (let eventI = 0; eventI < eventsDict[t].length; eventI++) {
          let currentId = eventsDict[t][eventI].id;
          if (currentId in eventToOverlapDict) {
            eventsDict[t][eventI]['concurrent'] = eventToOverlapDict[currentId];
          } else {
            eventsDict[t][eventI]['concurrent'] = 1;
          }
        }
      }

      this.maxConcurrent = countConcurrent;
      return eventsDict;
    },
    calcColSpan(eventElem) {
      if (eventElem['concurrent'] === 1) { return this.maxConcurrent; }
      return this.maxConcurrent / eventElem['concurrent'];
    },
    calcMaxWidth(numConcurrent) {
      return (60 / numConcurrent) + 'px';
    }
  },
  computed: {
    events: function() { // get events on this day
      let stringDate = this.getStringDate();
      let newEvents = [];

      for (let i = 0; i < this.info.length; i++) {
        if (this.info[i].date_utc.split(' ')[0] === stringDate) {
          newEvents.push(this.info[i]);
        }
      }
      return newEvents;
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.day {
  position: relative;
  padding-top: 36px;
  height: auto;
  width: 14.28%; /* 1/7 */
  border: 1px solid white;
  background-color: #efece5;
}
.weekViewDay {
  padding-top: 0px;
}
.greyed-out {
  background-color: #f8f8f8;
}
.highlighted {
  background-color: rgba(255, 199, 44, 0.2);
}

.date {
  position: absolute;
  top: 10px;
  left: 10px;
}

.events {
  position: relative;
  max-height: 140px;
  min-height: 90px;
  overflow-y: scroll;
  overflow-x: hidden;
}

/* --- weekly view (segment into hours) ---- */

.hours-table {
  width: 100%;
  border-collapse: collapse;
}

.hours-table tr {
  height: 28px;
  border-bottom: 0.5px dotted white;
  padding: 0px;
}

.hours-table td {
  padding: 0px;
}

.hour-line {
  border-bottom: 2px solid white !important;
}

.weeklyViewEvent {
  padding: 4px;
  margin: 0px;
  max-width: 100%;
  max-height: 100%;
  overflow-y: scroll;
  background-color: rgba(0, 179, 152, 0.3);
  border-left: 0.5px solid #efece5;
  border-right: 0.5px solid #efece5;
}
</style>
